import  { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";
import axiosServicesOnboarding from "../../../services/axiosServicesOnboarding";
import { Box, Typography } from "@mui/material";

const StepBox = ({ number, title }) => {
  return (
    <Box position="relative">
      <Box
        display="flex"
        alignItems="center"
        border="1px solid #e0e0e0"
        borderRadius="8px"
        padding="6px"
        bgcolor="#ffffff"
        position="relative"
      >
        {/* Number Circle */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="40px"
          height="40px"
          border="2px solid #001d7e"
          borderRadius="50%"
          marginRight="8px"
        >
          <Typography variant="body1" color="#001d7e" fontWeight="bold">
            {number}
          </Typography>
        </Box>

        {/* Title */}
        <Typography variant="body1" fontWeight="bold" color="#001d7e">
          {title}
        </Typography>

        {/* Pointed edge - border */}
        <Box
          position="absolute"
          right="-21px"
          top="50%"
          sx={{
            transform: "translateY(-50%)",
            borderLeft: "21px solid #e0e0e0",
            borderTop: "21px solid transparent",
            borderBottom: "21px solid transparent",
          }}
        />

        {/* Pointed edge - fill */}
        <Box
          position="absolute"
          right="-20px"
          top="50%"
          sx={{
            transform: "translateY(-50%)",
            borderLeft: "20px solid #ffffff",
            borderTop: "20px solid transparent",
            borderBottom: "20px solid transparent",
            zIndex: 1,
            height: "60px",
          }}
        />
      </Box>
    </Box>
  );
};

const TermsAndCondition = ({ onBack, data, refresh, setRefresh }) => {
  const [content, setContent] = useState(""); // State to store the plain text content
  const [loading, setLoading] = useState(true); // State to handle loading state
  const [error, setError] = useState(null); // State to handle errors

  const [isLoading, setIsLoading] = useState(false);

  // Function to extract plain text from HTML
  const extractPlainText = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Extract text content from the HTML
    return doc.body.textContent || "";
  };

  const getTermsAndCondition = async () => {
    try {
      // Fetch terms and condition
      const res = await axiosServicesOnboarding.get(
        "Customer/agreement-documents"
      );
      console.log("Terms and condition:", res);

      // Sanitize the HTML content using DOMPurify
      const sanitizedContent = DOMPurify.sanitize(res.data?.tac || "");

      // Extract plain text from the sanitized HTML
      const plainTextContent = extractPlainText(sanitizedContent);

      setContent(plainTextContent);
    } catch (error) {
      console.error("Error fetching terms and condition:", error);
      setError("Failed to load terms and conditions. Please try again later.");
    } finally {
      setLoading(false); // Set loading to false regardless of success or failure
    }
  };

  const onAgree = async () => {
    const payload = {
      requirementType: "TAC",
    };

    try {
      setIsLoading(true);
      // Add logic to agree to terms and conditions
      const res = await axiosServicesOnboarding.post(
        "Customer/complete-onboarding",
        payload
      );
      console.log("User agreed to terms and conditions.");
      toast.success(
        res?.data?.message || "Agreed to terms and conditions successfully!"
      );
      setRefresh(!refresh); // Refresh the parent component
      onBack(); // Go back to the previous step
      setIsLoading(false);
    } catch (error) {
      console.error("Error agreeing to terms and condition:", error);
      toast.error(
        error?.response?.data?.message ||
          "Failed to agree to terms and conditions."
      );
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getTermsAndCondition();
  }, []);

  return (
    <div className="w-full md:w-[550px] mx-auto p-">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2 text-gray-700 text-lg font-semibold">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
              stroke="#0E156F"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
              stroke="#0E156F"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Agreements</span>
        </div>
        <button onClick={onBack} className="text-gray-600 text-2xl">
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.0003 29.3337C23.3641 29.3337 29.3337 23.3641 29.3337 16.0003C29.3337 8.63653 23.3641 2.66699 16.0003 2.66699C8.63653 2.66699 2.66699 8.63653 2.66699 16.0003C2.66699 23.3641 8.63653 29.3337 16.0003 29.3337Z"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M20 12L12 20"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 12L20 20"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="px-8 py-2 bg-[#edf1f9] rounded-md mb-8">
        <Box maxWidth="600px" width="100%" p={2}>
          <StepBox number="01" title="Terms & Conditions" />
        </Box>
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-primary-color text-center mb-6">
        Our Terms & Conditions
      </h3>

      {/* Content Section */}
      <div className="flex items-center justify-center">
        <div className="bg-white max-w-2xl">
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-color"></div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center py-6">{error}</div>
          ) : (
            <div className="h-[550px] overflow-y-auto px-4 py-6">
              <div
                className="prose"
                dangerouslySetInnerHTML={{ __html: content }} // Render sanitized HTML
              ></div>
              {/* Agree Button */}
            </div>
          )}
          <button
            onClick={onAgree}
            className="mt-6 w-full bg-primary-color text-white py-4 px-4 rounded-lg hover:hover:bg-[#8388B6] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            disabled={loading || isLoading}
          >
            {isLoading ? "Loading..." : "I Agree"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsAndCondition;
